import { config } from '../config';
import { lotMetaStore, type LotMeta, type LotStatus } from './plots';

const KNOWN_STATUSES: ReadonlySet<string> = new Set([
  'available',
  'under-contract',
  'pending',
  'sold',
]);

function parseStatus(s: string | undefined): LotStatus | undefined {
  if (!s) return undefined;
  const norm = s.trim().toLowerCase().replace(/\s+/g, '-');
  return KNOWN_STATUSES.has(norm) ? (norm as LotStatus) : undefined;
}

// Bump this whenever the parse logic changes shape so old cached entries
// get discarded instead of feeding stale fields into the store.
const STORAGE_KEY = 'manglillo.lotMeta.v2';

// Pre-v2 keys. Read by nobody; removed once on load so the orphaned blob
// doesn't sit in every returning visitor's storage forever.
const LEGACY_STORAGE_KEYS = ['manglillo.lotMeta.v1'];

function readLocalCache(): Array<[string, LotMeta]> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    // Validate each entry's shape, not just the outer array: setAll()
    // destructures `const [k, v] of entries`, so one malformed element
    // throws. Previously that threw before the fetch below ever ran, which
    // meant a corrupt cache could never be replaced — the app was stuck
    // with it permanently.
    const wellFormed = parsed.every(
      (e) =>
        Array.isArray(e) &&
        typeof e[0] === 'string' &&
        e[1] !== null &&
        typeof e[1] === 'object',
    );
    return wellFormed ? (parsed as Array<[string, LotMeta]>) : null;
  } catch {
    return null;
  }
}

function writeLocalCache(entries: Array<[string, LotMeta]>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // private mode / quota — ignore, the SW cache still has it for next time
  }
}

/**
 * Populate `lotMetaStore` with per-lot dimensions:
 *   1. Hydrate synchronously from localStorage (~2.4 KB JSON) so the lot
 *      panel has data on the very first paint, even on a cold offline
 *      launch where the user never opened any lot online before.
 *   2. In the background, fetch the published Google Sheet CSV and update
 *      both the store and the localStorage cache so future loads are
 *      current.
 *
 * Silent on any failure — the UI falls back to polygon-computed values
 * for fields the CSV would normally supply.
 */
export async function loadLotMeta(): Promise<void> {
  // Hydration is guarded separately from the fetch: whatever happens with
  // the cached blob, the network refresh below must still run.
  try {
    const cached = readLocalCache();
    if (cached) lotMetaStore.setAll(cached);
  } catch {
    // malformed cache — fall through to the network
  }
  try {
    for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key);
  } catch {
    // private mode / quota — harmless
  }
  try {
    const res = await fetch(config.lotsMetaCsvUrl);
    if (!res.ok) return;
    const rows = parseCsv(await res.text());
    const entries: Array<[string, LotMeta]> = [];
    for (const row of rows) {
      const id = row['ID']?.trim();
      if (!id) continue;
      const meta: LotMeta = {
        id,
        lot: row['Lot']?.trim() || undefined,
        ancho: parseNum(row['Ancho']),
        oeste: parseNum(row['Oeste']),
        este:  parseNum(row['Este']),
        area:  parseNum(row['Area']),
        status: parseStatus(row['Status']),
      };
      entries.push([id, meta]);
    }
    if (entries.length > 0) {
      lotMetaStore.setAll(entries);
      writeLocalCache(entries);
    }
  } catch {
    // network / CORS / parse error — fall back to whatever we hydrated
    // from localStorage (or polygon-computed values if neither exists)
  }
}

function parseNum(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  let cleaned = s.trim();
  if (cleaned === '') return undefined;
  // The sheet mixes conventions: "11,353.55" uses comma as a thousands
  // separator, while "59,75" / "127,77" use comma as the DECIMAL separator.
  // Only strip commas when a dot is also present (true thousands sep);
  // otherwise treat the lone comma as a decimal point.
  if (cleaned.includes(',') && cleaned.includes('.')) cleaned = cleaned.replace(/,/g, '');
  else if (cleaned.includes(',')) cleaned = cleaned.replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/** Proper CSV parser — handles quoted fields with embedded commas. */
function parseCsv(text: string): Array<Record<string, string>> {
  const rows = parseRows(text);
  if (rows.length === 0) return [];
  // Normalise the header before it becomes the lookup keys. Google's export
  // can carry a UTF-8 BOM, and sheet headers pick up stray trailing spaces
  // (the live sheet already ships "Sucesion "). Either would make every
  // row['ID'] / row['Status'] lookup below return undefined — no error, no
  // console output, just every lot silently frozen on stale cached data.
  const header = rows[0].map((h) => h.replace(/^\uFEFF/, '').trim());
  const out: Array<Record<string, string>> = [];
  for (let i = 1; i < rows.length; i++) {
    const cols = rows[i];
    const obj: Record<string, string> = {};
    header.forEach((h, idx) => {
      obj[h] = cols[idx] ?? '';
    });
    out.push(obj);
  }
  return out;
}

/**
 * Split raw CSV text into rows of cells. Scans character by character rather
 * than splitting on newlines first, so a newline inside a quoted cell (a
 * multi-line note in the sheet) doesn't shear the record in half.
 */
function parseRows(text: string): string[][] {
  const src = text.replace(/\r\n/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"' && src[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(cur);
      cur = '';
    } else if (c === '\n') {
      row.push(cur);
      rows.push(row);
      row = [];
      cur = '';
    } else {
      cur += c;
    }
  }
  row.push(cur);
  rows.push(row);
  // Drop trailing all-empty rows left by a final newline.
  while (rows.length && rows[rows.length - 1].every((c) => c === '')) rows.pop();
  return rows;
}
